import { MailboxInfo } from 'owa-client-ids';
import { trace } from 'owa-trace';
import { getConnectorsLTIToken } from 'owa-connectors';
import { getActionEndpointUrlFromStore } from '../selectors/getActionEndpoint';
import { HttpReadyState, HttpStatusCode } from 'owa-http-status-codes';

const DATA_PREFIX = 'data:';
const BLOB_PREFIX = 'blob:';

/**
 * Method to fix the links of externally (3rd party) hosted images.
 * @param {HTMLElement} cardElement The Image element
 * @param {MailboxInfo} mailboxInfo The SMTP address
 * @param {string} originator? The card originator. Needed for downloading secure images.
 */
export default function fixExternalInlineImages(
    cardElement: HTMLElement,
    mailboxInfo: MailboxInfo,
    originator?: string
) {
    const externalImgElements = cardElement.querySelectorAll('img:not([src^="cid:"])');
    if (externalImgElements) {
        for (let i = 0; i < externalImgElements.length; i++) {
            // don't apply changes for data urls
            if (
                (<HTMLImageElement>externalImgElements[i]).src.indexOf(DATA_PREFIX) !== 0 &&
                (<HTMLImageElement>externalImgElements[i]).src.indexOf(BLOB_PREFIX) !== 0
            ) {
                fixSrcForExternalImage(
                    <HTMLImageElement>externalImgElements[i],
                    mailboxInfo.mailboxSmtpAddress,
                    originator
                );
            }
        }
    }
}

/**
 * Method to fix the source url of the image.
 * @param {HTMLElement} img The image element
 * @param {string} mailboxId The user mailpox info.
 * @param {string} originator The card originator.
 */
async function fixSrcForExternalImage(
    img: HTMLImageElement,
    mailboxId: string,
    originator: string
) {
    try {
        const initialImageUrl = `${getActionEndpointUrlFromStore()}/${mailboxId}/th?u=${encodeURIComponent(
            img.src
        )}`;
        const finalImageSrc = await getImageUrlRequest(initialImageUrl, mailboxId, originator);
        img.src = finalImageSrc;
    } catch (error) {
        trace.warn(
            '[ActionableMessageDebug] fixSrcForExternalImage: Error setting src value. Exception: ' +
                error.message
        );
    }
}

/**
 * Async function to get the new blob image url.
 * @param {string} imageUrl The original image url.
 * @param {string} smtpAddress The SMTP address of the user.
 * @returns {Promise<string>} The image blob url
 */
async function getImageUrlRequest(
    imageUrl: string,
    smtpAddress: string,
    originator: string
): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
        const token = await getConnectorsLTIToken();

        if (token) {
            const request = new XMLHttpRequest();
            request.open('GET', imageUrl);
            request.responseType = 'blob';

            request.setRequestHeader('Authorization', `Bearer ${token}`);
            request.setRequestHeader('X-AnchorMailbox', smtpAddress);
            if (originator) {
                request.setRequestHeader('X-OriginatorId', originator);
            }

            request.onreadystatechange = function () {
                if (request.readyState === HttpReadyState.Done) {
                    try {
                        let response = parseResponse(request);
                        resolve(response);
                    } catch (e) {
                        reject(e);
                    }
                }
            };

            request.send();
        } else {
            reject(new Error('token not generated'));
        }
    });
}

/**
 * Function to parse the response.
 * @param {XMLHttpRequest} request The XMLHttpRequest object containing the response and status code.
 * @returns {string} The new image blob url.
 */
function parseResponse(request: XMLHttpRequest): string {
    try {
        if (request.status == HttpStatusCode.OK) {
            const blob = request.response;
            const contentType = request.getResponseHeader('Content-Type');
            const isBlob =
                contentType &&
                contentType.toLowerCase().indexOf('image') === 0 &&
                blob instanceof Blob;

            if (isBlob) {
                return URL.createObjectURL(blob);
            }
        }
    } catch (error) {
        throw error;
    }

    return null;
}
